/**
 * Phase 9 Session D (gap XC-3) — informational amber banner shown to
 * operators who are accessing admin pages via the `orbit:admin` fallback
 * because the `rule_admin` Auth0 role has not yet been provisioned.
 *
 * Context: see `.claude/agent-memory/senior-frontend-engineer/` and
 * `project_rule_admin_role_pending.md` — `rule_admin` is declared in spec
 * 082/084/088c but not yet in the Auth0 tenant. `orbit:admin` is
 * explicitly accepted as a temporary fallback; this banner reminds
 * consultants of the pending provisioning work without blocking access.
 *
 * Dismissal persists for the current session only via sessionStorage —
 * a hard refresh or new tab re-shows the banner so the reminder never
 * fully goes away until the role is provisioned.
 */
import { useEffect, useState } from 'react';
import { Icon } from './ui/Icon.js';
import { useAuth } from '../lib/auth-context.js';

const DISMISS_KEY = 'beacon.roleProvisioningBanner.dismissed';

export function RoleProvisioningBanner() {
  const { hasRole } = useAuth();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Re-read sessionStorage when the component mounts in case another
  // admin page in the same tab already dismissed the banner before this
  // one rendered.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(DISMISS_KEY) === '1') {
        setDismissed(true);
      }
    } catch {
      /* sessionStorage unavailable — stay visible */
    }
  }, []);

  const isFallback = hasRole('orbit:admin') && !hasRole('rule_admin');

  if (!isFallback || dismissed) return null;

  const onDismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore — dismissal simply won't persist */
    }
  };

  return (
    <div
      data-testid="role-provisioning-banner"
      role="status"
      // Spec 248 (review P1-4): Kridt's Banner, not a filled amber panel.
      className="hub-banner"
      data-tone="warning"
    >
      <i className="bx bx-error hub-banner__icon" aria-hidden="true" />
      <div className="hub-banner__body">
        <p className="hub-banner__title">Using orbit:admin fallback</p>
        <p className="hub-banner__text">
          The <code>rule_admin</code>{' '}
          Auth0 role hasn't been provisioned yet. You're accessing this admin
          page with{' '}
          <code>orbit:admin</code>{' '}
          as a temporary fallback. Provision{' '}
          <code>rule_admin</code>{' '}
          in Auth0 to remove this banner.
        </p>
      </div>
      <button
        data-testid="role-provisioning-dismiss"
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss role provisioning banner"
        className="hub-round hub-banner__dismiss shrink-0"
      >
        <Icon name="bx-x" />
      </button>
    </div>
  );
}
